/**
 * Reading the assistant's live call: its control frames, where its socket
 * lives, how a turn's words are put together from their fragments, and which
 * of the questions it takes are the person's spoken ones.
 *
 * Pure, and apart from `assistant-live-socket.ts` for the reason
 * `live-wire.ts` gives: the socket reaches for `apiBaseUrl`, which pulls React
 * Native in, and a test cannot load it. The reader is loose on purpose: a
 * frame this build does not know is null, never a crash, so a server that adds a message next week is a phone that shrugs at
 * it today. docs/plans/done/orb-live-call.md, "The contract".
 */
import {
  isAssistantMicWhy,
  isAssistantTurnOutcome,
  LIVE_DUPLEX_MODES,
  LIVE_ID_MAX,
  readLiveServerFrame,
  type AssistantCardAction,
  type AssistantLiveContext,
  type AssistantLiveServerMessage,
  type AssistantTurnOutcome,
} from '@alluzo/shared';

import { liveWsUrl } from '@/lib/live-wire';

/**
 * The API host over ws, at the assistant's route. Built from `liveWsUrl` so
 * the http-to-ws rule lives in one place.
 */
export const assistantLiveWsUrl = (base: string): string => liveWsUrl(base).replace(/\/live$/, '/assistant/live');

/** A non-empty string no longer than the wire's id cap. */
const isLiveId = (value: unknown): value is string => typeof value === 'string' && value !== '' && value.length <= LIVE_ID_MAX;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parsed = (raw: string): unknown => {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
};

/**
 * Whether a thread message is one the conversation can draw: an id, a role the
 * SDK knows, and a list of parts. The server already kept these; the phone
 * only appends them, so a malformed one is dropped rather than handed to
 * `useChat`, where it would take the list down.
 */
const isThreadMessage = (value: unknown): boolean =>
  isObject(value) &&
  typeof value.id === 'string' &&
  (value.role === 'user' || value.role === 'assistant' || value.role === 'system') &&
  Array.isArray(value.parts);

/** The three things the voice can hear the person say about a card. */
const CARD_ACTIONS: readonly AssistantCardAction[] = ['confirm', 'leave', 'undo'];
const isCardAction = (value: unknown): value is AssistantCardAction => typeof value === 'string' && (CARD_ACTIONS as readonly string[]).includes(value);

export const readAssistantLiveServerMessage = (raw: unknown): AssistantLiveServerMessage | null => {
  const body = typeof raw === 'string' ? parsed(raw) : raw;
  if (!isObject(body) || typeof body.type !== 'string') return null;
  switch (body.type) {
    case 'open':
      return body.provider === 'google' || body.provider === 'openai'
        ? {
            type: 'open',
            provider: body.provider,
            greets: body.greets === true,
            // Version 2: a capable client's call names its duplex, and how to come back after a drop.
            ...(typeof body.duplex === 'string' && (LIVE_DUPLEX_MODES as readonly string[]).includes(body.duplex)
              ? { duplex: body.duplex as (typeof LIVE_DUPLEX_MODES)[number] }
              : {}),
            ...(isLiveId(body.callId) ? { callId: body.callId } : {}),
            ...(isLiveId(body.token) ? { token: body.token } : {}),
          }
        : null;
    case 'speaking':
      return { type: 'speaking' };
    case 'interrupted':
      return { type: 'interrupted' };
    case 'working':
      return { type: 'working' };
    case 'mic':
      // Live mode's hold on the mic (os-live-mode.md, Decided 3 and 4). The
      // reason is the one source: `held` is read from it, so the two never disagree.
      return isAssistantMicWhy(body.why) ? { type: 'mic', held: body.why !== 'open', why: body.why } : null;
    case 'heard':
      return typeof body.text === 'string' ? { type: 'heard', text: body.text, final: body.final === true } : null;
    case 'hearing':
      return typeof body.text === 'string' ? { type: 'hearing', text: body.text } : null;
    case 'said':
      return typeof body.text === 'string' ? { type: 'said', text: body.text, final: body.final === true } : null;
    case 'turn':
      return Array.isArray(body.messages)
        ? {
            type: 'turn',
            messages: body.messages.filter(isThreadMessage),
            card: body.card === true,
            outcome: isAssistantTurnOutcome(body.outcome) ? body.outcome : 'answered',
          }
        : null;
    case 'asking':
      return typeof body.messageId === 'string' && body.messageId !== '' && typeof body.text === 'string'
        ? { type: 'asking', messageId: body.messageId, text: body.text, queued: body.queued === true }
        : null;
    case 'progress':
      return typeof body.tool === 'string' && body.tool !== '' ? { type: 'progress', tool: body.tool, input: body.input === undefined ? {} : body.input } : null;
    case 'not_reached':
      return {
        type: 'not_reached',
        heard: typeof body.heard === 'string' ? body.heard : '',
        said: typeof body.said === 'string' ? body.said : '',
      };
    case 'confirm_card':
      return typeof body.toolCallId === 'string' && body.toolCallId !== '' && isCardAction(body.action)
        ? { type: 'confirm_card', toolCallId: body.toolCallId, action: body.action }
        : null;
    case 'error':
      return {
        type: 'error',
        code: typeof body.code === 'string' ? body.code : '',
        message: typeof body.message === 'string' ? body.message : '',
      };
    case 'closed':
      return {
        type: 'closed',
        reason: typeof body.reason === 'string' ? body.reason : '',
        ...(typeof body.ms === 'number' && Number.isFinite(body.ms) && body.ms >= 0 ? { ms: body.ms } : {}),
      };
    // Version 2, sent only to a client that said it is capable: read, and bounded, in the shared reader.
    case 'state':
    case 'line':
    case 'next':
    case 'replaced':
    case 'parked':
    case 'card_voice':
    case 'note':
    case 'elsewhere':
    case 'name_hint':
    case 'unsure':
    case 'partial':
    // The studio seat's frames (onboarding-harness 7.2); the web reads them through this file.
    case 'drafting':
    case 'work':
    case 'drafted':
    case 'point':
    case 'ask_hint':
    case 'build_sheet':
    case 'open_workspace':
      return readLiveServerFrame(body);
    default:
      return null;
  }
};

/**
 * Where the person is, as a `place` frame carries it: the screen, and the
 * record and the pinned cards only when the context has them. The thread,
 * its messages, the greeting and the language went with `start` and do not
 * move.
 */
export const placeOf = (context: AssistantLiveContext): Pick<AssistantLiveContext, 'screen' | 'recordId' | 'cards' | 'view'> => ({
  screen: context.screen,
  ...(context.recordId === undefined ? {} : { recordId: context.recordId }),
  ...(context.cards === undefined ? {} : { cards: context.cards }),
  ...(context.view === undefined ? {} : { view: context.view }),
});

/**
 * A turn's words once its `final` fragment lands, from the fragments already
 * joined and the final one's text.
 *
 * The two providers close a turn differently. OpenAI's final is the whole
 * transcript, the deltas again in one piece. Gemini's is the last fragment,
 * or nothing — and the server's translator sends an empty final of its own
 * when a turn ends with its words still open. So an empty final keeps the
 * fragments; a final that begins with what the fragments already said is the
 * whole; anything else is the last piece, added on. The same rule, for the
 * same reason, as `wordsOf` in the server's `call.ts`, with the Gemini case
 * the server does not need.
 */
export const turnWords = (parts: string, final: string): string => {
  const held = parts.trim();
  const last = final.trim();
  if (last === '') return held;
  if (held === '' || last.startsWith(held)) return last;
  return `${parts}${final}`.trim();
};

/**
 * The person's words of one exchange as the sheet follows them: the voice's
 * fragments so far, the transcriber's latest line, and whether the next word
 * starts a new exchange.
 */
export type HeardTrack = {
  /** The voice's own fragments of this exchange, joined. */
  readonly parts: string;
  /** The transcriber's latest line of this exchange, whole; null when none came. */
  readonly line: string | null;
  readonly fresh: boolean;
};

export const NEW_HEARD: HeardTrack = { parts: '', line: null, fresh: true };

/**
 * What moves the words: the transcriber's line (`hearing`), the voice's own
 * `heard` fragments, the assistant taking the question, or its turn landing.
 */
export type HeardEvent =
  | { readonly type: 'hearing'; readonly text: string }
  | { readonly type: 'heard'; readonly text: string; readonly final: boolean }
  | { readonly type: 'working' }
  | { readonly type: 'turn' };

/** The best words of an exchange: the transcriber's line while there is one, else the voice's fragments. */
const bestOf = (track: HeardTrack): string => (track.line ?? track.parts).trim();

/**
 * One step of the words. `hearing` is set when the live line changes (null
 * clears it); `heard` when an exchange's words are settled enough to keep
 * on screen; either is absent when it does not change.
 *
 * **The live line is the transcriber's while it has one.** The voice sends
 * the person's words only once they have stopped; the server's transcriber,
 * beside it, sends the whole utterance so far about every half second while
 * they talk (`hearing`), so each one REPLACES the line. The voice's own
 * fragments of the same exchange are still followed, but they never overwrite
 * a transcriber line: they arrive later and, on Gemini, as one late piece. A
 * call with no transcriber (a server without a Google key) draws the voice's
 * fragments as it always did.
 *
 * **The question is committed when the assistant takes it.** On Gemini the
 * voice never marks the person's words final before the answer: `working`
 * and then `turn` arrive, and the only final is the server translator's empty
 * one at the very end, after the voice has spoken. So `working` keeps the
 * best words so far as `heard` (a line or a fragment that still lands is
 * kept again), and `turn` keeps them once more and closes the exchange, so
 * the empty final after it cannot wipe them and the next utterance starts a
 * fresh line. OpenAI's words are final before the call, or arrive whole after
 * the turn, and both read as they always did.
 */
export const trackHeard = (
  track: HeardTrack,
  event: HeardEvent,
): { readonly track: HeardTrack; readonly hearing?: string | null; readonly heard?: string } => {
  const open = track.fresh ? NEW_HEARD : track;
  switch (event.type) {
    case 'hearing': {
      const line = event.text.trim();
      if (line === '') return { track };
      return { track: { ...open, line, fresh: false }, hearing: line };
    }
    case 'heard': {
      if (!event.final) {
        const parts = `${open.parts}${event.text}`;
        const next: HeardTrack = { ...open, parts, fresh: false };
        // A worse line never replaces a better one of the same exchange.
        if (open.line !== null) return { track: next };
        return { track: next, hearing: parts.trim() === '' ? null : parts.trim() };
      }
      const words = open.line ?? turnWords(open.parts, event.text);
      return { track: NEW_HEARD, hearing: null, ...(words === '' ? {} : { heard: words }) };
    }
    case 'working': {
      const held = bestOf(open);
      return { track, ...(held === '' ? {} : { heard: held }) };
    }
    case 'turn': {
      const held = bestOf(open);
      return { track: NEW_HEARD, hearing: null, ...(held === '' ? {} : { heard: held }) };
    }
  }
};

/**
 * The questions a call has met, for the hand-off tap (S33): a spoken question
 * the call takes to work on is one light tap, and a line of the phone's own
 * (`say`: a chip, a typed line) is none. A question is known by its id; the
 * phone's own line by its words, which the server echoes as that question's
 * (`sayWords`), so a spoken question and a typed line that cross — the server
 * then sends the spoken one first — are told apart whichever id comes first.
 */
export type AskingTrack = {
  /** Every question id met, queued or not. */
  readonly met: ReadonlySet<string>;
  /** The phone's own lines sent and not yet met as a question, in `sayWords`' form. */
  readonly lines: readonly string[];
};

export const NEW_ASKING: AskingTrack = { met: new Set(), lines: [] };

/**
 * A line's words as the server words the question it becomes: one space
 * between words, trimmed. An empty one is a line the server ignores, so it is
 * never sent.
 */
export const sayWords = (text: string): string => text.replace(/\s+/g, ' ').trim();

/** The phone sent a line of its own, in `sayWords`' form. */
export const trackSaid = (track: AskingTrack, words: string): AskingTrack => ({ ...track, lines: [...track.lines, words] });

/**
 * One `asking` frame. `tap`: the call has taken a spoken question to work on
 * — an id not met before, not queued, and not a line the phone sent, which
 * that id then takes. A question met queued is met: when it starts later,
 * the person is listening to the answer before it, not handing it over.
 */
export const trackAsking = (
  track: AskingTrack,
  asking: { readonly messageId: string; readonly text: string; readonly queued: boolean },
): { readonly track: AskingTrack; readonly tap: boolean } => {
  if (track.met.has(asking.messageId)) return { track, tap: false };
  const met = new Set(track.met).add(asking.messageId);
  const own = track.lines.indexOf(asking.text);
  if (own !== -1) return { track: { met, lines: [...track.lines.slice(0, own), ...track.lines.slice(own + 1)] }, tap: false };
  return { track: { ...track, met }, tap: !asking.queued };
};

/**
 * Whether a live turn went through Coach: its reply carries a tool part — a
 * read, a step, a card — where small talk the voice answered itself carries
 * only words. The sheet opens the conversation for the first and stays on the
 * voice for the second (docs/plans/done/orb-live-call.md, decision 5). Read from the
 * messages rather than a flag on the wire, so the rule is the thread's own.
 */
export const answeredByCoach = (messages: readonly { readonly role: string; readonly parts: readonly { readonly type: string }[] }[]): boolean =>
  messages.some((message) => message.role === 'assistant' && message.parts.some((part) => part.type.startsWith('tool-')));

/**
 * Whether a live `turn` is small talk the voice kept while Coach works on
 * another question: a typed line, a chip, Ask again, or a question promoted
 * from the queue. Each runs with the voice free (Phase 3.4), so the voice can
 * answer "okay thanks" by itself meanwhile, and the server folds that
 * exchange into the thread and sends it as a `turn` of its own. The phone
 * tells it from Coach's answer because no `asking` id matches it (Phase 2.3,
 * step 5): Coach's answer carries the person's message under the id its
 * `asking` named. Only an answered turn with messages is a fold; a stopped,
 * refused, empty or failed turn, or one with nothing in it, is Coach's.
 */
export const isVoiceFold = (
  askingId: string | null,
  turn: { readonly outcome: AssistantTurnOutcome; readonly messages: readonly unknown[] },
): boolean =>
  askingId !== null &&
  turn.outcome === 'answered' &&
  turn.messages.length > 0 &&
  !turn.messages.some((message) => isObject(message) && message.id === askingId);
